import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import TasksAPI from '../../../services/tasksService';
import TaskDetail from './TaskDetail';
import TaskCreateModal from '../../partial/TaskCreateModal';
import TaskOutcomeModal from '../../partial/TaskOutcomeModal';
import { isEngagementType, requiresOutcome, getTaskTypeMeta } from '../../../constants/taskTypeMeta';
import { taskErrorMessage, allowedOutcomesFrom, isOutcomeRequiredError } from '../../../util/taskErrors';
import { formatPhoneDisplay, telHref } from '../../../util/phoneUtils';
import { toast } from 'react-toastify';
import { HTTPService } from '../../../services/httpService';
import dateService from '../../../services/dateService';
import { isMultiLocation, isAllLocations, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';
import ReactGA from 'react-ga4';
import './Tasks.css';

const API = new HTTPService();

const PRIORITY_ICONS = {
  urgent: { cls: 'fa-sharp fa-solid fa-circle-exclamation', color: '#DC2626', title: 'Urgent' },
  high: { cls: 'fa-sharp fa-solid fa-arrow-up', color: '#EA580C', title: 'High' },
  normal: { cls: 'fa-sharp fa-solid fa-minus', color: '#2563EB', title: 'Normal' },
  low: { cls: 'fa-sharp fa-solid fa-arrow-down', color: '#6B7280', title: 'Low' }
};

class Tasks extends Component {
  state = {
    tasks: [],
    selectedTask: null,
    loading: true,
    filter: 'open',
    hiddenList: false,
    showCreateModal: false,
    staffList: [],
    refreshing: false,
    showFilterDropdown: false,
    outcomeTask: null,
    // Authoritative option list from a server 400, when it disagrees with ours.
    allowedOutcomes: null,
    outcomeSubmitting: false,
    outcomeError: null
  };

  async componentDidMount() {
    await Promise.all([
      this.fetchTasks(),
      this.fetchStaff()
    ]);

    // Check if we should select a specific task (from Today View navigation)
    const locationState = this.props.location?.state;
    if (locationState?.selectTaskId) {
      const task = this.state.tasks.find(t => t.task_id === locationState.selectTaskId);
      if (task) {
        this.setState({ selectedTask: task, hiddenList: true, fromToday: !!locationState.fromToday });
      }
      this.props.history.replace({ ...this.props.location, state: undefined });
    }

    window.addEventListener('obdClosed', this.handleObdClosed);
    // Server auto-resolves engagement tasks on estimate create/book/lost —
    // those flows dispatch refreshNavbarCounts, so refetch to reflect it.
    window.addEventListener('refreshNavbarCounts', this.handleObdClosed);
  }

  componentWillUnmount() {
    window.removeEventListener('obdClosed', this.handleObdClosed);
    window.removeEventListener('refreshNavbarCounts', this.handleObdClosed);
  }

  handleObdClosed = () => {
    this.fetchTasks();
  };

  fetchTasks = async () => {
    const { typeId, selectedLocationId, user } = this.props;
    const { filter } = this.state;

    this.setState({ loading: true });
    try {
      const params = {
        installerId: typeId,
        userId: user?.User_id,
        userRole: user?.role
      };
      if (filter !== 'all') params.status = filter;
      if (selectedLocationId && selectedLocationId !== 'all') {
        params.locationId = selectedLocationId;
      }
      const tasks = await TasksAPI.getAll(params);
      this.setState({ tasks: Array.isArray(tasks) ? tasks : [], loading: false });
    } catch (err) {
      console.error('Tasks: Error fetching tasks', err);
      this.setState({ tasks: [], loading: false });
    }
  };

  fetchStaff = async () => {
    try {
      const { data } = await API.get(`/users?company_id=${this.props.typeId}&user_type=installer`);
      const staff = Array.isArray(data) ? data : (data?.users || []);
      this.setState({ staffList: staff });
    } catch (err) {
      console.error('Tasks: Error fetching staff', err);
    }
  };

  handleRefresh = () => {
    this.setState({ refreshing: true });
    window.location.reload();
  };

  handleFilterChange = (filter) => {
    this.setState({ filter, selectedTask: null, hiddenList: false }, this.fetchTasks);
  };

  handleSelectTask = (task, mobile = false) => {
    this.setState({ selectedTask: task, hiddenList: mobile });
  };

  handleCancel = () => {
    if (this.state.fromToday) {
      this.props.history.push('/today');
      return;
    }
    this.setState({ hiddenList: false, selectedTask: null });
  };

  handleComplete = async (taskId, userId) => {
    // Some task types require an outcome — intercept with the picker instead of
    // completing directly. NOT isEngagementType: invoice_recovery is gated too.
    const task = this.state.tasks.find(t => t.task_id === taskId) || this.state.selectedTask;
    if (task && requiresOutcome(task.task_type)) {
      this.setState({ outcomeTask: task, outcomeError: null, allowedOutcomes: null });
      return;
    }
    try {
      await TasksAPI.complete(taskId, userId);
      await this.fetchTasks();
      this.setState({ selectedTask: null, hiddenList: false });
      window.dispatchEvent(new CustomEvent('refreshNavbarCounts'));
      ReactGA.event({ category: 'Reminder', action: 'Completed Reminder', label: 'prod' });
    } catch (err) {
      console.error('Tasks: Error completing task', err);
      // A gate this client does not know about yet — open the picker with the
      // server's own list rather than leaving the click with no visible effect.
      if (task && isOutcomeRequiredError(err)) {
        this.setState({
          outcomeTask: task,
          outcomeError: taskErrorMessage(err),
          allowedOutcomes: allowedOutcomesFrom(err),
        });
        return;
      }
      toast.error(taskErrorMessage(err, 'Could not complete the task. Please try again.'));
    }
  };

  handleOutcomeConfirm = async ({ resolution_type, completion_note }) => {
    const { outcomeTask } = this.state;
    const { user } = this.props;
    this.setState({ outcomeSubmitting: true, outcomeError: null });
    try {
      await TasksAPI.complete(outcomeTask.task_id, user?.User_id, { resolution_type, completion_note });
      await this.fetchTasks();
      this.setState({ outcomeTask: null, outcomeSubmitting: false, selectedTask: null, hiddenList: false });
      window.dispatchEvent(new CustomEvent('refreshNavbarCounts'));
      ReactGA.event({ category: 'Reminder', action: 'Completed Reminder', label: 'prod' });
    } catch (err) {
      console.error('Tasks: Error completing engagement task', err);
      if (err.response?.status === 404) {
        // Task was auto-resolved elsewhere — treat as done.
        await this.fetchTasks();
        this.setState({ outcomeTask: null, outcomeSubmitting: false, selectedTask: null, hiddenList: false });
        window.dispatchEvent(new CustomEvent('refreshNavbarCounts'));
        return;
      }
      this.setState({
        outcomeSubmitting: false,
        outcomeError: taskErrorMessage(err, 'Could not complete the task. Please choose an outcome and try again.'),
        allowedOutcomes: allowedOutcomesFrom(err) || this.state.allowedOutcomes,
      });
    }
  };

  handleUpdate = async (taskId, payload) => {
    try {
      const updated = await TasksAPI.update(taskId, payload);
      await this.fetchTasks();
      const refreshedTask = this.state.tasks.find(t => t.task_id === taskId) || updated;
      this.setState({ selectedTask: refreshedTask });
      ReactGA.event({ category: 'Reminder', action: 'Updated Reminder', label: 'prod' });
    } catch (err) {
      console.error('Tasks: Error updating task', err);
      toast.error(taskErrorMessage(err, 'Could not save the changes. Please try again.'));
    }
  };

  handleSnooze = async (taskId, snoozeUntil) => {
    try {
      await TasksAPI.snooze(taskId, snoozeUntil);
      await this.fetchTasks();
      this.setState({ selectedTask: null, hiddenList: false });
      window.dispatchEvent(new CustomEvent('refreshNavbarCounts'));
      ReactGA.event({ category: 'Reminder', action: 'Snoozed Reminder', label: 'prod' });
    } catch (err) {
      console.error('Tasks: Error snoozing task', err);
      toast.error(taskErrorMessage(err, 'Could not snooze the task. Please try again.'));
    }
  };

  handleDelete = async (taskId) => {
    try {
      await TasksAPI.cancel(taskId);
      await this.fetchTasks();
      this.setState({ selectedTask: null, hiddenList: false });
      window.dispatchEvent(new CustomEvent('refreshNavbarCounts'));
      ReactGA.event({ category: 'Reminder', action: 'Deleted Reminder', label: 'prod' });
    } catch (err) {
      console.error('Tasks: Error cancelling task', err);
      toast.error(taskErrorMessage(err, 'Could not cancel the task. Please try again.'));
    }
  };

  handleTaskCreated = () => {
    this.fetchTasks();
    window.dispatchEvent(new CustomEvent('refreshNavbarCounts'));
    ReactGA.event({ category: 'Reminder', action: 'Created Reminder', label: 'prod' });
  };

  getSortedTasks() {
    const { tasks } = this.state;
    return [...tasks].sort((a, b) => {
      const now = moment();
      const aLocal = a.due_date ? dateService.fromAPIDate(a.due_date) : null;
      const bLocal = b.due_date ? dateService.fromAPIDate(b.due_date) : null;
      const aOverdue = aLocal && aLocal.isBefore(now, 'day') && a.status !== 'completed';
      const bOverdue = bLocal && bLocal.isBefore(now, 'day') && b.status !== 'completed';
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;

      if (aLocal && bLocal) {
        const diff = aLocal.diff(bLocal);
        if (diff !== 0) return diff;
      }
      if (aLocal && !bLocal) return -1;
      if (!aLocal && bLocal) return 1;

      const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
      return (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
    });
  }

  getStaffName(userId) {
    const { staffList } = this.state;
    if (!userId || !staffList.length) return null;
    const staff = staffList.find(s => String(s.User_id || s.user_id) === String(userId));
    return staff ? (staff.user_name || staff.name || `${staff.First_name || ''} ${staff.Last_name || ''}`.trim()) : null;
  }

  iconPriority = (priority) => {
    const p = PRIORITY_ICONS[priority] || PRIORITY_ICONS.normal;
    return <i className={p.cls} title={p.title} style={{ color: p.color }} />;
  };

  renderFilterDropdown() {
    const { filter, showFilterDropdown } = this.state;
    const isActive = filter !== 'open';

    const options = [
      { key: 'open', label: 'Open' },
      { key: 'completed', label: 'Completed' },
      { key: 'all', label: 'All' }
    ];

    return (
      <div style={{ position: 'relative' }}>
        <button
          className={`btn btn-sm btn-header-icon${isActive ? ' filter-active' : ''}`}
          onClick={() => this.setState({ showFilterDropdown: !showFilterDropdown })}
          title="Filter list"
        >
          <i className={`fa-sharp ${isActive ? 'fa-solid' : 'fa-regular'} fa-filter`}></i>
        </button>
        {showFilterDropdown && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }}
              onClick={() => this.setState({ showFilterDropdown: false })} />
            <div className="filter-dropdown-menu">
              {options.map(opt => (
                <div
                  key={opt.key}
                  className={`filter-item${filter === opt.key ? ' active' : ''}`}
                  onClick={() => this.setState({ filter: opt.key, showFilterDropdown: false, selectedTask: null, hiddenList: false }, this.fetchTasks)}
                >
                  <span>{opt.label}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  renderTaskRow(task) {
    const { locations } = this.props;
    const multiLoc = isMultiLocation(locations);
    const allLocView = isAllLocations(this.props.selectedLocationId);
    const localDue = task.due_date ? dateService.fromAPIDate(task.due_date) : null;
    const isOverdue = localDue && localDue.isBefore(moment(), 'day') && task.status !== 'completed';
    const assigneeName = task.assigned_to_name || this.getStaffName(task.assigned_to_user_id || task.assigned_to);
    const engagement = isEngagementType(task.task_type);
    const typeMeta = engagement ? getTaskTypeMeta(task.task_type) : null;
    const reminderAt = task.task_type === 'consultation' && task.reminder_at
      ? moment(task.reminder_at, 'YYYY-MM-DD HH:mm') : null;

    return (
      <small>
        <div className="row">
          <div className="col-2">
            {localDue ? (
              <b className={localDue.isSame(moment(), 'day') ? 'date-today' : (isOverdue ? 'text-danger' : '')}>
                {localDue.format("ddd MMM D")}
              </b>
            ) : (
              <b className="text-muted">No date</b>
            )}
            {reminderAt && reminderAt.isValid() && (
              <div style={{ fontWeight: 600, color: isOverdue ? undefined : 'var(--color-text-secondary)' }}>
                {reminderAt.format('h:mm A')}
              </div>
            )}
          </div>
          <div className="col-8">
            <div className="row">
              <div className="col">
                <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                  {typeMeta && (
                    <i className={typeMeta.icon} title={typeMeta.label} style={{ color: typeMeta.accent, marginRight: 6 }} />
                  )}
                  {task.title}
                  {task.task_type === 'inbound_call' && task.call_count > 1 && (
                    <span
                      className="badge ml-1"
                      title={`Contacted ${task.call_count} times`}
                      style={{ backgroundColor: '#DC2626', color: '#fff', fontSize: '0.7rem', verticalAlign: 'middle' }}
                    >
                      &times;{task.call_count}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col">
                {task.task_type === 'inbound_call' && task.caller_phone_e164 ? (
                  <>
                    {task.entity_name ? `${task.entity_name} — ` : ''}
                    <a
                      href={telHref(task.caller_phone_e164)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: 'var(--color-info)', fontWeight: 600 }}
                    >
                      <i className="fa-sharp fa-regular fa-phone mr-1" />
                      {formatPhoneDisplay(task.caller_phone_e164)}
                    </a>
                  </>
                ) : (
                  task.entity_name || assigneeName || 'Unassigned'
                )}
              </div>
            </div>
            {task.description && (
              <div className="row">
                <div
                  className="col text-muted"
                  style={engagement ? {
                    // Inbound-call descriptions are an append-only timeline — keep newlines, clamp to 3 lines
                    fontSize: '0.85em', whiteSpace: 'pre-line', overflow: 'hidden',
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'
                  } : {
                    fontSize: '0.85em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                  }}
                >
                  {task.description}
                </div>
              </div>
            )}
          </div>
          <div className="col-2 text-center">
            {this.iconPriority(task.priority)}
            {multiLoc && allLocView && task.location_id && (() => {
              const loc = locations.find(l => String(l.location_id) === String(task.location_id));
              return loc ? (
                <div style={{
                  fontSize: '0.65rem', fontWeight: 600, color: '#fff',
                  backgroundColor: getLocationBadgeColor(task.location_id, locations),
                  borderRadius: '3px', padding: '1px 4px', marginTop: '2px',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  {getLocationName(loc)}
                </div>
              ) : null;
            })()}
          </div>
        </div>
      </small>
    );
  }

  render() {
    const { selectedTask, loading, showCreateModal, staffList } = this.state;
    const { user, typeId, selectedLocationId, locations } = this.props;
    const sortedTasks = this.getSortedTasks();
    const hasSelection = !!selectedTask;

    return (
      <React.Fragment>
        {/* Mobile header */}
        <div className="d-flex align-items-center justify-content-between d-lg-none d-sm-block p-2 list-header">
          <div className="d-flex align-items-center" style={{ gap: '4px' }}>
            {this.renderFilterDropdown()}
            <button
              className="btn btn-sm btn-header-icon"
              onClick={this.handleRefresh}
              disabled={this.state.refreshing}
              title="Refresh list"
            >
              <i className={`fa-sharp fa-regular fa-refresh ${this.state.refreshing ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
          <h3 className="page-name mb-0">Reminders</h3>
          {hasSelection ? (
            <button
              type="button"
              className="btn btn-sm btn-header-icon"
              onClick={this.handleCancel}
            >
              <i className="fa-sharp fa-regular fa-times"></i>
            </button>
          ) : (
            <span style={{ width: '32px' }}></span>
          )}
        </div>

        <div className="row">
          {/* Desktop list */}
          <div className="col-lg-4 text-center d-none d-lg-block">
            <div className="d-flex align-items-center justify-content-between p-3 mb-0 corners list-header">
              <span style={{ width: '32px' }}></span>
              <h5 className="page-name mb-0">Reminders</h5>
              <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                {this.renderFilterDropdown()}
                <button
                  className="btn btn-sm btn-header-icon"
                  onClick={this.handleRefresh}
                  disabled={this.state.refreshing}
                  title="Refresh list"
                >
                  <i className={`fa-sharp fa-regular fa-refresh ${this.state.refreshing ? 'fa-spin' : ''}`}></i>
                </button>
              </div>
            </div>
            <ul className="list-group-fluid p-0 list-group-flush shadow bg-white corners">
              <li className="list-group-item d-flex justify-content-center align-items-center corners">
                <button
                  type="button"
                  onClick={() => this.setState({ showCreateModal: true })}
                  className="jv-btn jv-btn-primary"
                >
                  <i className="fa-sharp fa-regular fa-plus" /> New Reminder
                </button>
              </li>

              {loading ? (
                <li className="list-group-item text-center p-4">
                  <i className="fa-sharp fa-solid fa-spinner fa-spin" /> Loading...
                </li>
              ) : sortedTasks.length === 0 ? (
                <li className="list-group-item text-center p-4 text-muted">
                  {this.state.filter === 'open' ? 'No open reminders' : this.state.filter === 'completed' ? 'No completed reminders' : 'No reminders'}
                </li>
              ) : (
                sortedTasks.map(task => (
                  <li
                    onClick={() => this.handleSelectTask(task)}
                    key={task.task_id}
                    className={task.task_id === selectedTask?.task_id ? "list-group-item active" : "list-group-item"}
                  >
                    {this.renderTaskRow(task)}
                  </li>
                ))
              )}
            </ul>
          </div>

          {/* Mobile list */}
          {this.state.hiddenList === false && (
            <div className="col-lg-4 text-center d-lg-none d-sm-block">
              <ul className="list-group-fluid p-0 list-group-flush shadow bg-white">
                <li className="list-group-item d-flex justify-content-center align-items-center">
                  <button
                    type="button"
                    onClick={() => this.setState({ showCreateModal: true })}
                    className="btn btn-success btn-lg mt-2 mb-2"
                  >
                    NEW REMINDER
                  </button>
                </li>

                {loading ? (
                  <li className="list-group-item text-center p-4">
                    <i className="fa-sharp fa-solid fa-spinner fa-spin" /> Loading...
                  </li>
                ) : sortedTasks.length === 0 ? (
                  <li className="list-group-item text-center p-4 text-muted">
                    {this.state.filter === 'open' ? 'No open reminders' : this.state.filter === 'completed' ? 'No completed reminders' : 'No reminders'}
                  </li>
                ) : (
                  sortedTasks.map(task => (
                    <li
                      onClick={() => this.handleSelectTask(task, true)}
                      key={task.task_id}
                      className={task.task_id === selectedTask?.task_id ? "list-group-item active" : "list-group-item"}
                    >
                      {this.renderTaskRow(task)}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}

          {/* Detail column: Desktop always visible, mobile only when selected */}
          <div className={!hasSelection ? "col-lg-8 d-none d-lg-block" : "col-lg-8"}>
            {!hasSelection && (
              <h5 className="text-center p-3">Select a reminder to view details.</h5>
            )}

            {hasSelection && (
              <React.Fragment>
                <div className="shadow-sm p-2 corners detail-panel-selected">
                  {this.state.fromToday ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm mb-2"
                      onClick={this.handleCancel}
                    >
                      <i className="fa-sharp fa-regular fa-arrow-left mr-2"></i>
                      Back to Today
                    </button>
                  ) : (
                    <button type="button" className="close d-none d-lg-block" onClick={this.handleCancel} aria-label="Close">
                      <span aria-hidden="true">&times;</span>
                    </button>
                  )}
                  <br />
                  <TaskDetail
                    task={selectedTask}
                    onUpdate={this.handleUpdate}
                    onComplete={this.handleComplete}
                    onSnooze={this.handleSnooze}
                    onDelete={this.handleDelete}
                    user={user}
                    staffList={staffList}
                    locations={locations}
                    selectedLocationId={selectedLocationId}
                  />
                </div>
              </React.Fragment>
            )}
          </div>
        </div>

        {/* Create Modal */}
        <TaskCreateModal
          show={showCreateModal}
          onHide={() => this.setState({ showCreateModal: false })}
          onCreated={this.handleTaskCreated}
          installerId={typeId}
          userId={user.User_id}
          locationId={selectedLocationId}
          staffList={staffList}
        />

        {/* Outcome picker — required for engagement task completion */}
        <TaskOutcomeModal
          show={!!this.state.outcomeTask}
          task={this.state.outcomeTask}
          onHide={() => this.setState({ outcomeTask: null, outcomeError: null })}
          onConfirm={this.handleOutcomeConfirm}
          submitting={this.state.outcomeSubmitting}
          error={this.state.outcomeError}
          allowedOutcomes={this.state.allowedOutcomes}
        />
      </React.Fragment>
    );
  }
}

export default withRouter(Tasks);
